import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Captions, Check, Copy, Download, FileText, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { appFetch, uploadFile } from '@/shared/api/app-client'
import type { ApiResponse, AssetUploadResult, TranscriptResult } from '@/shared/api/app-types'

type TranscriptionTask = {
  status: 'transcribing' | 'analyzing' | 'completed' | 'failed'
  transcript?: TranscriptResult
  error?: string
}

export function TranscriptionCanvas({ initialTaskId, initialClippingTaskId }: { initialTaskId?: string; initialClippingTaskId?: string }) {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('字幕与转写项目')
  const [sourceUrl, setSourceUrl] = useState('')
  const [uploadedFile, setUploadedFile] = useState<{ file: File; name: string } | null>(null)
  const [notes, setNotes] = useState('')
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [taskId, setTaskId] = useState<string | null>(initialTaskId ?? null)
  const [clippingTaskId, setClippingTaskId] = useState<string | null>(initialClippingTaskId ?? null)
  const [job, setJob] = useState<TranscriptionTask | null>(null)
  const [copied, setCopied] = useState(false)
  const [subtitleFormat, setSubtitleFormat] = useState<'srt' | 'vtt' | 'ass'>('srt')
  const [savingSubtitle, setSavingSubtitle] = useState(false)
  const [savedSubtitle, setSavedSubtitle] = useState<AssetUploadResult | null>(null)

  useEffect(() => {
    if (initialTaskId) setTaskId(initialTaskId)
  }, [initialTaskId])

  useEffect(() => {
    if (initialClippingTaskId) setClippingTaskId(initialClippingTaskId)
  }, [initialClippingTaskId])

  useEffect(() => {
    const activeTaskId = taskId ?? clippingTaskId
    if (!activeTaskId) return
    let cancelled = false
    const poll = async () => {
      try {
        const response = await appFetch<ApiResponse<TranscriptionTask>>(
          clippingTaskId
            ? `/api/clipping/jobs/${clippingTaskId}`
            : `/api/subsite/transcription/aliyun/${taskId}`,
        )
        if (cancelled) return
        setJob(response.data)
        if (response.data.status === 'failed') setError(response.data.error ?? '转写失败')
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : '读取转写进度失败')
      }
    }
    void poll()
    const timer = window.setInterval(() => {
      void poll()
    }, 3_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [taskId, clippingTaskId])

  async function handleFile(file: File | undefined) {
    if (!file) return
    if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
      setError('请选择视频或音频文件')
      return
    }
    setUploading(true)
    setError(null)
    try {
      setUploadedFile({ file, name: file.name })
      setSourceUrl('')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '文件上传失败')
    } finally {
      setUploading(false)
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!uploadedFile) {
      setError('请上传视频或音频文件。公开视频链接解析暂未接入转录服务。')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const response = await uploadFile<ApiResponse<{ taskId: string }>>(
        '/api/subsite/transcription/aliyun',
        uploadedFile.file,
        undefined,
        { language: 'zh' },
      )
      setTaskId(response.data.taskId)
      setJob(null)
      navigate({ to: '/app/tools/transcription', search: { taskId: response.data.taskId } })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '创建转写项目失败')
    } finally {
      setSubmitting(false)
    }
  }

  const transcriptText =
    job?.transcript?.sentences.map((sentence) => sentence.text).join('\n') ?? ''

  async function copyTranscript() {
    if (!transcriptText) return
    await navigator.clipboard.writeText(transcriptText)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  function createSubtitleFile() {
    if (!job?.transcript) return null
    const normalizedTitle = (title.trim() || 'fastcut-transcript').replace(/[\\/:*?"<>|]/g, '_')
    const content = buildSubtitle(job.transcript.sentences, subtitleFormat)
    const mimeType =
      subtitleFormat === 'srt'
        ? 'application/x-subrip'
        : subtitleFormat === 'vtt'
          ? 'text/vtt'
          : 'text/x-ass'
    return new File([content], `${normalizedTitle}.${subtitleFormat}`, { type: mimeType })
  }

  function downloadSubtitle() {
    const file = createSubtitleFile()
    if (!file) return
    const url = URL.createObjectURL(file)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
    URL.revokeObjectURL(url)
  }

  async function saveSubtitleToAssets() {
    const file = createSubtitleFile()
    if (!file || savingSubtitle) return
    setSavingSubtitle(true)
    try {
      const response = await uploadFile<ApiResponse<AssetUploadResult>>('/api/assets/upload', file)
      setSavedSubtitle(response.data)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '字幕文件导入素材库失败')
    } finally {
      setSavingSubtitle(false)
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:px-7 sm:py-10">
      <div className="mb-8 flex items-center gap-4">
        <Link
          to="/app/ai-create"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground hover:bg-accent"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">字幕与转写</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            导入视频或音频，创建可继续编辑的字幕与转写项目。
          </p>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-2xl border border-border bg-card p-5 sm:p-6"
        >
          <div className="space-y-2">
            <Label htmlFor="transcription-title">项目名称</Label>
            <Input
              id="transcription-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-3">
            <Label htmlFor="transcription-url">视频或音频链接</Label>
            <Input
              id="transcription-url"
              value={sourceUrl}
              disabled
              onChange={(event) => setSourceUrl(event.target.value)}
              placeholder="公开视频链接解析暂未接入"
            />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              或上传本地文件
              <span className="h-px flex-1 bg-border" />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,audio/*"
              className="hidden"
              onChange={(event) => void handleFile(event.target.files?.[0])}
            />
            {uploadedFile ? (
              <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
                <span className="truncate text-foreground">{uploadedFile.name}</span>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setUploadedFile(null)}
                >
                  移除
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-5 py-9 text-center transition-colors hover:border-primary/50 hover:bg-accent"
              >
                <Upload className="size-6 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  {uploading ? '上传中…' : '上传视频或音频文件'}
                </span>
                <span className="text-xs text-muted-foreground">
                  支持 MP4、MOV、MP3、WAV；免费用户总空间 100 MB
                </span>
              </button>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="transcription-notes">处理说明（可选）</Label>
            <Textarea
              id="transcription-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="例如：保留中英文混合内容、生成逐字字幕"
              className="min-h-24"
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button
            type="submit"
            disabled={submitting || uploading || job?.status === 'transcribing'}
            className="gap-2"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Captions className="size-4" />
            )}
            {submitting ? '正在提交转写…' : '开始转写'}
          </Button>
        </form>
        <aside className="rounded-2xl border border-border bg-card p-5">
          <FileText className="size-6 text-primary" />
          <h2 className="mt-4 text-base font-semibold text-foreground">
            {job?.status === 'transcribing' ? '正在进行智能语音转写' : '转写结果'}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {job?.status === 'transcribing'
              ? '任务已提交。你可以留在这里等待，完成后会自动显示完整转录内容。'
              : '识别结果会按语句与时间顺序展示，可直接复制后继续制作字幕。'}
          </p>
          {job?.transcript ? (
            <div className="mt-5 text-xs text-muted-foreground">
              时长 {Math.round(job.transcript.durationMs / 1000)} 秒 · 语言{' '}
              {job.transcript.language}
            </div>
          ) : null}
        </aside>
      </div>

      {job?.status === 'transcribing' ? (
        <section className="mt-8 flex items-center gap-4 rounded-2xl border border-primary/25 bg-primary/5 px-5 py-5 sm:px-6">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
            <Loader2 className="size-5 animate-spin text-primary" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-foreground">正在转录中…</h2>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              正在提取语音并整理为带时间戳的文本。通常需要几分钟，完成后此页面会自动显示结果。
            </p>
          </div>
        </section>
      ) : null}

      {job?.transcript ? (
        <section className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-foreground">转录内容</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                共 {job.transcript.sentences.length} 段，可导出为专业字幕文件。
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void copyTranscript()}
              className="gap-1.5"
            >
              {copied ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
              {copied ? '已复制' : '复制内容'}
            </Button>
          </div>
          <div className="mt-5 flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <div className="text-sm font-medium text-foreground">生成字幕文件</div>
              <p className="mt-1 text-xs text-muted-foreground">
                使用当前的逐句时间戳生成 SRT、WebVTT 或 ASS 字幕。
              </p>
            </div>
            <select
              value={subtitleFormat}
              onChange={(event) => setSubtitleFormat(event.target.value as typeof subtitleFormat)}
              className="h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground"
            >
              <option value="srt">SRT</option>
              <option value="vtt">WebVTT (.vtt)</option>
              <option value="ass">ASS</option>
            </select>
            <Button variant="outline" size="sm" onClick={downloadSubtitle} className="gap-1.5">
              <Download className="size-3.5" />
              下载
            </Button>
            <Button
              size="sm"
              onClick={() => void saveSubtitleToAssets()}
              disabled={savingSubtitle}
              className="gap-1.5"
            >
              {savingSubtitle ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Upload className="size-3.5" />
              )}
              {savingSubtitle ? '导入中…' : '导入素材库'}
            </Button>
          </div>
          {savedSubtitle ? (
            <p className="mt-3 text-xs text-primary">已导入素材库：{savedSubtitle.fileName}</p>
          ) : null}
          <div className="mt-5 max-h-[460px] space-y-3 overflow-y-auto rounded-xl border border-border bg-background p-4">
            {job.transcript.sentences.map((sentence, index) => (
              <div
                key={`${sentence.startMs}-${index}`}
                className="grid gap-2 border-b border-border pb-3 last:border-0 last:pb-0 sm:grid-cols-[80px_1fr]"
              >
                <span className="text-xs tabular-nums text-primary">
                  {formatTime(sentence.startMs)}
                </span>
                <p className="text-sm leading-6 text-foreground/90">{sentence.text}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

function formatTime(milliseconds: number) {
  const seconds = Math.floor(milliseconds / 1000)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

function buildSubtitle(
  sentences: Array<{ text: string; startMs: number; endMs: number }>,
  format: 'srt' | 'vtt' | 'ass',
) {
  if (format === 'vtt') {
    return `WEBVTT\n\n${sentences.map((sentence) => `${formatTimestamp(sentence.startMs, '.', true)} --> ${formatTimestamp(sentence.endMs, '.', true)}\n${sentence.text}`).join('\n\n')}\n`
  }
  if (format === 'ass') {
    const header =
      '[Script Info]\nTitle: JIAN Transcript\nScriptType: v4.00+\n\n[V4+ Styles]\nFormat: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding\nStyle: Default,Arial,48,&H00FFFFFF,&H000000FF,&H00101010,&H80000000,0,0,0,0,100,100,0,0,1,2,1,2,80,80,50,1\n\n[Events]\nFormat: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text\n'
    return `${header}${sentences.map((sentence) => `Dialogue: 0,${formatAssTimestamp(sentence.startMs)},${formatAssTimestamp(sentence.endMs)},Default,,0,0,0,,${sentence.text.replace(/\n/g, '\\N')}`).join('\n')}\n`
  }
  return `${sentences.map((sentence, index) => `${index + 1}\n${formatTimestamp(sentence.startMs, ',', true)} --> ${formatTimestamp(sentence.endMs, ',', true)}\n${sentence.text}`).join('\n\n')}\n`
}

function formatTimestamp(milliseconds: number, separator: ',' | '.', includeHours: boolean) {
  const total = Math.max(0, Math.floor(milliseconds))
  const hours = Math.floor(total / 3_600_000)
  const minutes = Math.floor((total % 3_600_000) / 60_000)
  const seconds = Math.floor((total % 60_000) / 1_000)
  const ms = total % 1_000
  const prefix = includeHours ? `${String(hours).padStart(2, '0')}:` : ''
  return `${prefix}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}${separator}${String(ms).padStart(3, '0')}`
}

function formatAssTimestamp(milliseconds: number) {
  const total = Math.max(0, Math.floor(milliseconds))
  const hours = Math.floor(total / 3_600_000)
  const minutes = Math.floor((total % 3_600_000) / 60_000)
  const seconds = Math.floor((total % 60_000) / 1_000)
  const centiseconds = Math.floor((total % 1_000) / 10)
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`
}
